import dayjs from 'dayjs'
import {
  IncidentReportWithCase,
  IncidentReportUpdateCaseBody,
  IncidentReportCreateCaseBody,
  IncidentReportItem
} from '@lmc/ts-common'
import { incidentReportsConstants } from 'locale/incidentReports'
import { getCombinedTimeIso } from '../utils'
import {
  IncidentReportEditCaseForm,
  IncidentReportCreateCaseForm
} from '../components/forms/IncidentCaseForm'
import { AttachmentFieldType } from '../components/UploadAttachmentsOnSaveInput'

const normalizeNoDetailsField = (value: unknown): string | null => {
  if (!value) return null
  if (typeof value === 'string' && value.trim() === '') return null
  return value as string
}

const normalizeNoDetailsByStatus = (values: any) => {
  const result = { ...values }

  if (result.restrictivePracticesStatus === 'yes') {
    result.noRestraintsDetails = null
  }

  if (result.injuriesStatus === 'yes') {
    result.noInjuriesDetails = null
  }

  if (result.damagesStatus === 'yes') {
    result.noDamagesDetails = null
  }

  return result
}

// Helper to normalise "details" style objects before sending to the API.
// Contract: always send a DetailsObject when the API expects one:
// - If the field is "off" (hasBeenApplied is falsy OR we force the field off),
//   we send exactly `{ hasBeenApplied: false }` (no `details`).
// - If the field is "on", we keep the object as-is but ensure hasBeenApplied is true.
const normalizeDetailsObject = (value: any, forceOff: boolean = false): any => {
  // If there's no value and we are not explicitly forcing it off,
  // leave it alone (undefined/null will be handled by the caller).
  if (!value) {
    return forceOff ? { hasBeenApplied: false } : value
  }

  const isApplied = Boolean(value.hasBeenApplied) && !forceOff

  if (!isApplied) {
    // Explicitly represent the "off" state with just hasBeenApplied: false
    return { hasBeenApplied: false }
  }

  // When applied, keep other props (like details/body map) but normalise the flag.
  return {
    ...value,
    hasBeenApplied: true
  }
}

// Normalise all relevant section objects (restrictive practices, injuries,
// damages, medications) based on both the individual hasBeenApplied flags and
// the top-level section status radios (restrictivePracticesStatus, injuriesStatus,
// damagesStatus). This runs *only* at submit time so we don't affect the
// in-form UX while the user is toggling values on/off.
const normalizeIncidentCaseValues = <T extends { [key: string]: any }>(
  values: T
): T => {
  const restrictiveOff = values.restrictivePracticesStatus === 'no'
  const injuriesOff = values.injuriesStatus === 'no'
  const damagesOff = values.damagesStatus === 'no'

  return {
    ...values,
    physicalIntervention: normalizeDetailsObject(
      values.physicalIntervention,
      restrictiveOff
    ),
    restraints: normalizeDetailsObject(values.restraints, restrictiveOff),
    chemicalRestraints: normalizeDetailsObject(
      values.chemicalRestraints,
      restrictiveOff
    ),
    environmentalRestraints: normalizeDetailsObject(
      values.environmentalRestraints,
      restrictiveOff
    ),
    verbalRestraints: normalizeDetailsObject(
      values.verbalRestraints,
      restrictiveOff
    ),
    residentInjuries: normalizeDetailsObject(
      values.residentInjuries,
      injuriesOff
    ),
    carersInjuries: normalizeDetailsObject(values.carersInjuries, injuriesOff),
    otherPartiesInjuries: normalizeDetailsObject(
      values.otherPartiesInjuries,
      injuriesOff
    ),
    damages: normalizeDetailsObject(values.damages, damagesOff),
    damagesIntroducingRisk: normalizeDetailsObject(
      values.damagesIntroducingRisk,
      damagesOff
    ),
    // Medications are not tied to a section-level radio, but should still clear
    // details when "hasBeenApplied" is false.
    prnMedication: normalizeDetailsObject(values.prnMedication, false)
  } as T
}

export const prepareUpdateInitialValues = (
  incidentReport: IncidentReportWithCase,
  t: (key: string) => string
): Partial<IncidentReportEditCaseForm<string>> => {
  return {
    ...incidentReport.case,
    attachments: incidentReport.attachments.map((attachmentUrl, index) => ({
      name: `${t(incidentReportsConstants.FILE)} ${index + 1}`,
      isDeleted: false,
      isNew: false,
      url: attachmentUrl
    })),
    serviceUsersInvolved: incidentReport.case.serviceUsersInvolved.map(
      (serviceUser) => serviceUser.id
    ),
    carersInvolved: incidentReport.case.carersInvolved?.map(
      (serviceUser) => serviceUser.id
    ),
    severity: incidentReport.severity,
    log: {
      ...incidentReport.log,
      goals: incidentReport.log.goals?.map((goal) => goal.id) || [],
      dateLogged: dayjs(incidentReport.log.timeLogged),
      timeLogged: dayjs(incidentReport.log.timeLogged),
      item: incidentReport.log.item?.id
    }
  }
}

export const prepareUpdateBody = (
  values: IncidentReportEditCaseForm<string>
): IncidentReportUpdateCaseBody => {
  const normalized = normalizeNoDetailsByStatus(
    normalizeIncidentCaseValues(values)
  )

  return {
    severity: normalized.severity,
    serviceUsersInvolved: normalized.serviceUsersInvolved,
    otherPeopleInvolved: normalized.otherPeopleInvolved,
    otherWitnesses: normalized.otherWitnesses,
    carersInvolved: normalized.carersInvolved,
    physicalIntervention: normalized.physicalIntervention,
    restraints: normalized.restraints,
    chemicalRestraints: normalized.chemicalRestraints,
    environmentalRestraints: normalized.environmentalRestraints,
    verbalRestraints: normalized.verbalRestraints,
    residentInjuries: normalized.residentInjuries,
    carersInjuries: normalized.carersInjuries,
    otherPartiesInjuries: normalized.otherPartiesInjuries,
    damages: normalized.damages,
    damagesIntroducingRisk: normalized.damagesIntroducingRisk,
    prnMedication: normalized.prnMedication,
    noRestraintsDetails: normalizeNoDetailsField(
      normalized.noRestraintsDetails
    ),
    noInjuriesDetails: normalizeNoDetailsField(normalized.noInjuriesDetails),
    noDamagesDetails: normalizeNoDetailsField(normalized.noDamagesDetails),
    serviceUserOpinion: normalized.serviceUserOpinion,
    incidentOtherInformation: normalized.incidentOtherInformation,
    debriefingNotes: normalized.debriefingNotes,
    rootCause: normalized.rootCause,
    preventativeActions: normalized.preventativeActions,
    otherNotes: normalized.otherNotes,
    log: {
      ...normalized.log,
      timeLogged: getCombinedTimeIso(
        normalized?.log?.dateLogged,
        normalized?.log?.timeLogged
      )
    },
    followUpActions: {
      ...normalized.followUpActions,
      actions: normalized?.followUpActions?.actions?.map(
        (action: {
          id?: string
          title: string
          status: string
          assignedUserId?: string | null
        }) => ({
          id: action.id,
          title: action.title,
          status: action.status,
          assignedUserId: action.assignedUserId ?? null
        })
      )
    }
  } as any as IncidentReportUpdateCaseBody
}

export const getDeletedAttachmentsIndexes = (
  attachments: AttachmentFieldType[]
): number[] => {
  return attachments
    .map((attachment, index) =>
      attachment.isDeleted === true ? index : undefined
    )
    .filter((index): index is number => index !== undefined)
}

export const prepareCreateInitialValues = (
  incidentReport: IncidentReportItem
): Partial<IncidentReportCreateCaseForm<string>> => {
  return {
    ...incidentReport,
    severity: null,
    otherPeopleInvolved: null,
    physicalIntervention: { hasBeenApplied: false },
    restraints: { hasBeenApplied: false },
    chemicalRestraints: { hasBeenApplied: false },
    environmentalRestraints: { hasBeenApplied: false },
    verbalRestraints: { hasBeenApplied: false },
    residentInjuries: { hasBeenApplied: false },
    carersInjuries: { hasBeenApplied: false },
    otherPartiesInjuries: { hasBeenApplied: false },
    damages: { hasBeenApplied: false },
    damagesIntroducingRisk: { hasBeenApplied: false },
    prnMedication: { hasBeenApplied: false },
    noRestraintsDetails: null,
    noInjuriesDetails: null,
    noDamagesDetails: null,
    otherClientsInvolved: [],
    serviceUsersInvolved: [],
    attachments: [],
    followUpActions: {
      actions: [],
      notificationRequired: false,
      safeguardingActionsRequired: false
    },
    log: {
      ...incidentReport.log,
      goals: incidentReport.log.goals?.map((goal) => goal.id) || [],
      dateLogged: dayjs(incidentReport.log.timeLogged),
      timeLogged: dayjs(incidentReport.log.timeLogged),
      item: incidentReport.log.item?.id
    }
  }
}

export const prepareCreateBody = (
  values: IncidentReportCreateCaseForm<string>
): IncidentReportCreateCaseBody => {
  const normalized = normalizeNoDetailsByStatus(
    normalizeIncidentCaseValues(values)
  )

  const {
    noRestraintsDetails,
    noInjuriesDetails,
    noDamagesDetails,
    noInjuriesOccurred,
    damagesStatus,
    injuriesStatus,
    restrictivePracticesStatus,
    ...rest
  } = normalized as any

  return {
    ...(rest as unknown as IncidentReportCreateCaseBody),
    // keep the "no*Details" fields in the payload, but drop UI-only flags
    noRestraintsDetails: normalizeNoDetailsField(noRestraintsDetails),
    noInjuriesDetails: normalizeNoDetailsField(noInjuriesDetails),
    noDamagesDetails: normalizeNoDetailsField(noDamagesDetails),
    log: {
      ...normalized.log,
      timeLogged: getCombinedTimeIso(
        normalized?.log?.dateLogged,
        normalized?.log?.timeLogged
      )
    },
    followUpActions: {
      ...normalized.followUpActions,
      actions: normalized?.followUpActions?.actions?.map(
        (action: {
          title: string
          status: string
          assignedUserId?: string | null
        }) => ({
          title: action.title,
          status: action.status,
          assignedUserId: action.assignedUserId ?? null
        })
      )
    }
  } as any as IncidentReportCreateCaseBody
}
